import { ModuleEnum } from '../enums/ModuleEnum';
import { DatabaseClientInterface } from '../sql/DatabaseClientInterface';
import { AttributesFilterInputInterface } from '../interfaces/AttributesFilterInputInterface';
import { AttributeUniqValuesInterface } from '../interfaces/AttributeUniqValuesInterface';
import { ProductWithReplacementsInterface } from '../interfaces/ProductWithReplacementsInterface';
import { ProductReplacementInterface } from '../interfaces/ProductReplacementInterface';
import { buildWhereFromFilters } from '../sql/buildWhereFromFilters';
import { buildWhereByModule } from '../sql/buildWhereByModule';
import { AttributesClassWithCountInterface } from '../interfaces/AttributesClassWithCountInterface';
import { toPlaceholdersString } from '../sql/toPlaceholdersString';
import { ProductInterface } from '../interfaces/ProductInterface';
import { AttributesClassInterface, LocalizedAttributeNames } from '../interfaces/AttributesClassInterface';
import { LocalesEnum } from '../enums/LocalesEnum';
import { AttributeValueWithCountInterface } from '../interfaces/AttributeValueWithCountInterface';
import { RegionsEnum } from '../enums/RegionsEnum';
import { createReplacementConfig, ReplacementAttributes, ReplacementAttrName } from './createReplacementConfig';
import { OrderByInterface } from '../interfaces/OrderByInterface';
import { buildNumericOrder, buildStringOrder } from '../sql/buildOrderSql';
import { buildWhereByShoppingCenter } from '../sql/buildWhereByShoppingCenter';
import { prepareValuesMapping } from '../sql/prepareValuesMapping';
import { buildUniqueValuesSql } from '../sql/buildUniqueValuesSql';

export const discontinuedMapping: Record<RegionsEnum, LocalizedAttributeNames> = {
    [RegionsEnum.eu]: '474',
    [RegionsEnum.ap]: '472',
    [RegionsEnum.am]: '470',
}

export class ProductsResolver {
    private readonly replacementMappings: Record<ModuleEnum, ReplacementAttributes>;
    private readonly discontinuedAttr: LocalizedAttributeNames;

    constructor(
        private readonly db: DatabaseClientInterface,
        private readonly mapToProduct: (row: any) => ProductInterface,
        private readonly mapToAttributesClass: (product: ProductInterface) => AttributesClassInterface,
        private readonly mapCount: (arg: any) => number,
        private readonly mapValues: (
            locale: LocalesEnum,
            rawName: LocalesEnum,
        ) => (arg: any) => AttributeValueWithCountInterface,
        private readonly region: RegionsEnum,
    ) {
        this.replacementMappings = createReplacementConfig(this.region);
        this.discontinuedAttr = discontinuedMapping[this.region];
    }

    async values(
        product: ProductWithReplacementsInterface,
        id: string,
        module: ModuleEnum,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<AttributeUniqValuesInterface> {
        const labels = product.replacements
        .reduce((acc, curr) => [
            ...acc,
            ...curr.replacement.reduce((a, c) => [...a, c.Label], [] as string[])
        ], [] as string[]);

        return this.valuesInner(
            id,
            module,
            filter,
            labels,
            rawName,
            locale,
            shoppingCenter,
        );
    }

    async valveValues(
        product: ProductWithReplacementsInterface,
        id: string,
        module: ModuleEnum,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<AttributeUniqValuesInterface> {
        const labels = product.valveReplacements
        .reduce((acc, curr) => [
            ...acc,
            ...curr.replacement.reduce((a, c) => [...a, c.Label,
            ], [] as string[])], [] as string[]);

        return this.valuesInner(
            id,
            module,
            filter,
            labels,
            rawName,
            locale,
            shoppingCenter,
        );
    }

    async valuesInner(
        id: string,
        module: ModuleEnum,
        filter: AttributesFilterInputInterface,
        labels: string[],
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<AttributeUniqValuesInterface> {
        const {
            exclude,
            localized,
            attrValue
        } = prepareValuesMapping(id, rawName);

        const [whereModule, paramsModule] = buildWhereByModule(module, rawName, this.discontinuedAttr);
        const [whereFilter, paramsFilter] = buildWhereFromFilters(filter, exclude, rawName);
        const [whereShop, paramsShop] = buildWhereByShoppingCenter(rawName, shoppingCenter);
        const query = buildUniqueValuesSql(
            localized,
            attrValue,
            'product',
            `${whereModule}
                    ${whereFilter.length > 0 ? ` AND ${whereFilter}` : ''}
                    ${whereShop.length > 0 ? ` AND ${whereShop}` : ''}
                    ${labels.length ? ` AND label IN (${labels.map(() => '?').join(',')})` : ''}`
        )
        const params = [...paramsModule, ...paramsFilter, ...paramsShop, ...labels];
        const values = await this.db.getMany(
            query,
            params,
            this.mapValues(locale, rawName)
        );
        return { values };
    }

    async product(
        module: ModuleEnum,
        label: string,
        id: string,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
        filter: AttributesFilterInputInterface = { values: [] },
        valveFilter: AttributesFilterInputInterface = { values: [] },
    ): Promise<ProductWithReplacementsInterface | null> {
        const [shopWhere, shopParams] =  buildWhereByShoppingCenter(rawName, shoppingCenter);
        const product = await this.db.getOne(`
            SELECT id, label, attributes
            FROM product
            WHERE label = ? ${id ? `AND id = ?` : ``} ${shopWhere ? `AND ${shopWhere}` : ''}
            limit 1
        `, [label, ...(id ? [id] : []), ...shopParams], this.mapToProduct);
        // @TODO add filter by module
        if (!product) {
            return null;
        }

        const {
            replacementResult,
            replacementsCount
        } = await this.fetchActuatorReplacements(product, filter, module, rawName, locale, shoppingCenter);

        const { valveReplacements, valveReplacementsCount } = await (
            module === ModuleEnum.waterByValve
                ? this.fetchValveReplacements(product, valveFilter, rawName, locale, shoppingCenter)
                : Promise.resolve({ valveReplacements: [], valveReplacementsCount: 0 })
        );

        return {
            product: product.getAttributes(),
            replacements: replacementResult,
            replacementsCount,
            valveReplacements,
            valveReplacementsCount,
            values: {
                values: [],
            },
            valveValues: {
                values: [],
            },
        };
    }


    private async fetchActuatorReplacements(
        product: ProductInterface,
        filter: AttributesFilterInputInterface,
        module: ModuleEnum,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<{ replacementResult: ProductReplacementInterface[]; replacementsCount: number }> {
        const replacementAttributes = this.replacementMappings[module];
        const isDiscontinued = `${product.getRaw(this.discontinuedAttr)[0] ?? '0'}` === '1';
        const isSelfReplacementRequired = module !== ModuleEnum.waterByValve
            && product.isSolution(rawName) && !isDiscontinued;

        if (isSelfReplacementRequired) {
            return {
                replacementResult: [{
                    replacement: [this.mapToAttributesClass(product)],
                    accessories: [],
                    note: [],
                },
                    { replacement: [], accessories: [], note: [] },
                    { replacement: [], accessories: [], note: [] },
                    { replacement: [], accessories: [], note: [] },
                ],
                replacementsCount: 1,
            };
        }

        const replacementResult: ProductReplacementInterface[] = [];

        const replacements = await this.fetchRelated(
            product,
            replacementAttributes.map(([attr]) => attr),
            filter,
            rawName,
            shoppingCenter,
        );

        const replacementsCount = await this.countRelated(
            product,
            replacementAttributes.map(([attr]) => attr),
            filter,
            rawName,
            shoppingCenter,
        );

        const accessories = await this.fetchAccessories(
            product,
            replacementAttributes.map(([, attrs]) => attrs),
            rawName,
            shoppingCenter,
        );

        const notes = replacementAttributes.map(([, , note]) => {
            if (!note) {
                return [];
            }
            return product.getLocalized(note, locale);
        });

        replacementAttributes.forEach((_, index) => {
            replacementResult.push({
                replacement: replacements[index].map(this.mapToAttributesClass),
                accessories: accessories[index].map(this.mapToAttributesClass),
                note: notes[index],
            });
        });

        return { replacementResult, replacementsCount };
    }

    public async filterCount(
        module: ModuleEnum,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        shoppingCenter: string,
    ): Promise<number> {
        const [where, params] = buildWhereFromFilters(filter, [], rawName);
        const [moduleWhere, paramsModule] = buildWhereByModule(module, rawName, this.discontinuedAttr);
        const [shopWhere, shopParams] =  buildWhereByShoppingCenter(rawName, shoppingCenter);
        const count = await this.db.getOne(
            `SELECT count(*) as count
             FROM product
             WHERE ${moduleWhere} 
                 ${where.length > 0 ? ` AND ${where}` : ''}
                 ${shopWhere.length > 0 ? ` AND ${shopWhere}` : ''}
             `,
            [...paramsModule, ...params, ...shopParams],
            this.mapCount
        );
        return count ? count : 0;
    }

    public async filter(
        module: ModuleEnum,
        offset: number,
        limit: number,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        shoppingCenter: string,
        orderByNumber: OrderByInterface[] = [],
        orderByString: OrderByInterface[] = [],
        defaultOrder: string = 'label',
    ): Promise<AttributesClassWithCountInterface> {
        const [where, params] = buildWhereFromFilters(filter, [], rawName);
        const [moduleWhere, paramsModule] = buildWhereByModule(module, rawName, this.discontinuedAttr);
        const [shopWhere, shopParams] =  buildWhereByShoppingCenter(rawName, shoppingCenter);
        const count = await this.filterCount(module, filter, rawName, shoppingCenter);
        const orderNumber = buildNumericOrder(orderByNumber, rawName);
        const orderString = buildStringOrder(orderByString, rawName);
        const results = await this.db.getMany(
            `SELECT id, label, attributes
             FROM product
             WHERE ${moduleWhere} 
                 ${where.length > 0 ? ` AND ${where}` : ''}
                 ${shopWhere.length > 0 ? ` AND ${shopWhere}` : ''}
             ORDER BY ${[...orderNumber, ...orderString, defaultOrder].join(', ')}
             LIMIT ${limit} OFFSET ${offset ?? 0}`,
            [...paramsModule, ...params, ...shopParams],
            this.mapToProduct
        );
        return {
            count,
            results: results.map((product) => product.getAttributes()),
        };
    }

    private async fetchAccessories(
        product: ProductInterface,
        attrs: Array<ReplacementAttrName>[],
        rawName: LocalesEnum,
        shoppingCenter: string,
    ): Promise<ProductInterface[][]> {
        const queries = attrs.map((values) => this.fetchRelated(
            product,
            values,
            { values: [] },
            rawName,
            shoppingCenter,
        ));
        const results = await Promise.all(queries);
        return results.map((inner) => inner.reduce((p, c) => [...p, ...c], []));
    }

    private async fetchRelated(
        product: ProductInterface,
        attrs: Array<ReplacementAttrName>,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        shoppingCenter: string,
    ): Promise<ProductInterface[][]> {
        const queries = attrs.map((attr) => {
            const value = product.getRaw(attr);
            if (value.length === 0) {
                return [];
            }
            const [where, params] = buildWhereFromFilters(filter, [], rawName);
            const [shopWhere, shopParams] =  buildWhereByShoppingCenter(rawName, shoppingCenter);
            const sql = `SELECT id, label, attributes
                         FROM product
                         WHERE label IN (${toPlaceholdersString(value)}) 
                             ${where.length > 0 ? ` AND ${where}` : ''}
                             ${shopWhere.length > 0 ? ` AND ${shopWhere}` : ''}
                         `;
            return this.db.getMany(
                sql,
                [...value, ...params, ...shopParams],
                this.mapToProduct,
            );
        });
        return Promise.all(queries);
    }

    private async countRelated(
        product: ProductInterface,
        attrs: Array<ReplacementAttrName>,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        shoppingCenter: string,
    ): Promise<number> {
        const queries = attrs.map((attr) => {
            const value = product.getRaw(attr);
            if (value.length === 0) {
                return Promise.resolve(0);
            }
            const [where, params] = buildWhereFromFilters(filter, [], rawName);
            const [shopWhere, shopParams] =  buildWhereByShoppingCenter(rawName, shoppingCenter);

            return this.db.getOne(
                `SELECT count(*) as count
                 FROM product
                 WHERE label IN (${toPlaceholdersString(value)}) 
                     ${where.length > 0 ? ` AND ${where}` : ''}
                     ${shopWhere.length > 0 ? ` AND ${shopWhere}` : ''}
                 `,
                [...value, ...params, ...shopParams],
                this.mapCount,
            );
        });
        const results = await Promise.all(queries);
        return results.reduce<number>((acc, curr) => acc + (curr === null ? 0 : curr), 0);
    }

    private async fetchValveReplacements(
        product: ProductInterface,
        filter: AttributesFilterInputInterface,
        rawName: LocalesEnum,
        locale: LocalesEnum,
        shoppingCenter: string,
    ): Promise<{ valveReplacementsCount: number; valveReplacements: ProductReplacementInterface[] }> {

        const replacementAttributes = this
            .replacementMappings[ModuleEnum.ValveSolution];

        const isDiscontinued = `${product.getRaw(this.discontinuedAttr)[0] ?? '0'}` === '1';
        const isSelfReplacementRequired = product.isSolution(rawName) && !isDiscontinued;

        if (isSelfReplacementRequired) {
            return {
                valveReplacementsCount: 1,
                valveReplacements: [{
                    replacement: [this.mapToAttributesClass(product)],
                    accessories: [],
                    note: [],
                }],
            };
        }

        const valveReplacements: ProductReplacementInterface[] = [];

        const replacements = await this.fetchRelated(
            product,
            replacementAttributes.map(([attr]) => attr),
            filter,
            rawName,
            shoppingCenter,
        );

        const valveReplacementsCount = await this.countRelated(
            product,
            replacementAttributes.map(([attr]) => attr),
            filter,
            rawName,
            shoppingCenter,
        );

        const notes = replacementAttributes.map(([, , note]) => {
            if (!note) {
                return [];
            }
            return product.getLocalized(note, locale);
        });

        replacementAttributes.forEach((_, index) => {
            valveReplacements.push({
                replacement: replacements[index].map(this.mapToAttributesClass),
                accessories: [],
                note: notes[index],
            });
        });

        return { valveReplacementsCount, valveReplacements };
    }

}
